import { useState, useEffect, useRef, useMemo } from 'react';
import { LIMITES } from '../../../../utils/constantes';

export const useSignalTableLogic = (onSignalsChange, initialSignals = [], entradaFacilitada = false) => {
    const [signals, setSignals] = useState([]);

    const lastInitialSignalsRef = useRef(null);
    const updatingFromInitialRef = useRef(false);

    // Calcular o limite de sinais baseado em entradaFacilitada
    const signalLimit = useMemo(() => {
        return entradaFacilitada ? 2 : LIMITES.SINAIS;
    }, [entradaFacilitada]);

    const getSignalDescription = (index) => {
        if (index === 0) return "Sinal Ato";
        return `Sinal ${index}`;
    };

    // Efeito para inicializar sinais a partir de props
    useEffect(() => {
        // Evita atualizações circulares verificando se os sinais iniciais são diferentes
        const initialSignalsJson = JSON.stringify(initialSignals);
        if (
            initialSignals?.length > 0 &&
            initialSignalsJson !== lastInitialSignalsRef.current &&
            !updatingFromInitialRef.current
        ) {
            lastInitialSignalsRef.current = initialSignalsJson;
            updatingFromInitialRef.current = true;
            const formattedSignals = initialSignals.map((signal, index) => {
                // Extract nested ternary into individual statements
                let value = 0;
                if (signal.value !== undefined) {
                    value = signal.value;
                } else if (signal.Valor !== undefined) {
                    value = signal.Valor;
                }

                return {
                    id: signal.id || `auto_${Date.now()}_${index}`,
                    description: signal.description || getSignalDescription(index),
                    // Mantido como número: parseFloat evita a corrupção do parseCurrency (formato BR) com Number.toString().
                    value: parseFloat(value) || 0
                };
            });

            setSignals(formattedSignals);

            // Permitir novas atualizações após esta rodada de renderização
            setTimeout(() => {
                updatingFromInitialRef.current = false;
            }, 0);
        }
    }, [initialSignals]);

    // Notificar quando os sinais mudarem localmente
    useEffect(() => {
        // Evitar notificar durante a inicialização de props
        if (onSignalsChange && !updatingFromInitialRef.current) {
            onSignalsChange(signals);
        }
    }, [signals, onSignalsChange]);

    const addSignal = (valor = 0) => {
        if (signals.length >= signalLimit) return;

        const newSignal = {
            id: Date.now(),
            description: getSignalDescription(signals.length),
            value: valor
        };

        setSignals(prevSignals => [...prevSignals, newSignal]);
    };

    const removeSignal = (idToRemove) => {
        setSignals(prevSignals => {
            const updatedSignals = prevSignals.filter(signal => signal.id !== idToRemove);

            return updatedSignals.map((signal, index) => ({
                ...signal,
                description: getSignalDescription(index)
            }));
        });
    };

    const updateSignalValue = (id, newValue) => {
        setSignals(prevSignals =>
            prevSignals.map(signal =>
                signal.id === id
                    ? { ...signal, value: parseFloat(newValue) || 0 }
                    : signal
            )
        );
    };

    const isLimitReached = signals.length >= signalLimit;

    return {
        signals,
        addSignal,
        removeSignal,
        updateSignalValue,
        isLimitReached
    };
};